import React, { FC, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { Stack } from './Stack';
import { Button } from './Button';
import { Input } from './Input';
import { Label } from './Label';
import { ErrorMessage } from './ErrorMessage';
import { RadioButtonGroup } from './RadioButtonGroup';
import { TAB_ORDER } from '../utils';
import { lineHeight, inputWidth } from '../style';

const Row = styled.div`
  display: flex;
`;
const Col = styled.div`
  display: flex;
  flex-direction: column;
  &:not(:last-child) {
    margin-right: ${2 * lineHeight}px;
  }
`;
const Field = styled.div`
  margin: ${lineHeight}px 0;
  width: ${inputWidth}px;
`;
const SubmitButton = styled(Button)`
  width: ${inputWidth}px;
`;
const FieldErrorMessage = styled(ErrorMessage)`
  width: ${inputWidth}px;
`;
const Buttons = styled(Row)`
  margin: ${lineHeight}px 0 0;
  flex-direction: column;
  gap: ${lineHeight}px;
`;

export type ValidatedFormProps = {
  onSubmit: (values: { [name: string]: string }) => Promise<string | null>;
  loading?: boolean;
  setLoading?: (loading: boolean) => void;
  buttonText: string;
  fields: {
    [name: string]: {
      label: string;
      type?: string;
      pattern?: RegExp;
      options?: { [value: string]: string };
      validate?: (
        value: string,
        values: { [key: string]: string },
      ) => string | null;
      autoFocus?: boolean;
    };
  };
  layout?: string[][];
  initialValues?: { [name: string]: string };
  additionalButtons?: ReactNode;
  renderInfo?: (values: { [name: string]: string }) => ReactNode;
};

export const ValidatedForm: FC<ValidatedFormProps> = ({
  onSubmit,
  loading,
  setLoading,
  buttonText,
  fields,
  layout = [Object.keys(fields)],
  initialValues = {},
  renderInfo,
  additionalButtons,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [values, setValues] = useState<{ [name: string]: string }>(
    initialValues,
  );

  const errors = Object.entries(fields).reduce((memo, [name, { validate }]) => {
    if (validate) memo[name] = validate(values[name], values);
    return memo;
  }, {} as { [name: string]: string | null });
  const valid = Object.values(errors).every(v => v === null);

  return (
    <form
      onSubmit={async e => {
        e.preventDefault();
        setSubmitAttempted(true);
        if (valid) {
          setLoading && setLoading(true);
          const serverError = await onSubmit(values);
          setServerError(serverError);
          setLoading && setLoading(false);
        }
      }}
    >
      <Row>
        {layout.map(col => (
          <Col>
            {col.map((name, i) => {
              const { label, type, pattern, options, autoFocus } = fields[name];
              return (
                <Field key={name}>
                  <Label htmlFor={name}>{label}</Label>
                  {type === 'radio' && options ? (
                    <RadioButtonGroup
                      name={name}
                      value={values[name]}
                      options={options}
                      onChange={e => {
                        setValues({ ...values, [name]: e.target.value });
                      }}
                    />
                  ) : (
                    <Input
                      autoFocus={autoFocus}
                      disabled={loading}
                      name={name}
                      id={name}
                      type={type}
                      tabIndex={TAB_ORDER.INPUT}
                      value={values[name] ?? ''}
                      onChange={e => {
                        if (
                          pattern === undefined ||
                          e.target.value.match(pattern)
                        ) {
                          setValues({ ...values, [name]: e.target.value });
                        }
                      }}
                    />
                  )}
                  {submitAttempted && errors[name] && (
                    <FieldErrorMessage htmlFor={name}>
                      {errors[name]}
                    </FieldErrorMessage>
                  )}
                </Field>
              );
            })}
          </Col>
        ))}
      </Row>
      {serverError && <ErrorMessage as="span">{serverError}</ErrorMessage>}
      <Buttons>
        <SubmitButton
          type="submit"
          disabled={loading}
          tabIndex={TAB_ORDER.INPUT}
        >
          {buttonText}
        </SubmitButton>
        {additionalButtons && <Stack>{additionalButtons}</Stack>}
      </Buttons>
      {renderInfo && renderInfo(values)}
    </form>
  );
};
